import { BusinessObjectPartContext } from '@skylab-shared/core';
import { AdvanceSummary, ExchangeRateModel, SaleAdvanceDocumentItemModel, SaleDocumentItemModel, SaleDocumentKind, VatSummary, VatTableItemModel } from '@skylab-shared/dataModel';
import { UnboxCollection } from '@skylab-shared/types';
import { Calc, MidpointRounding } from '@skylab-shared/utils';
import { CalculatedDocument, DocumentAdjustmentCalculator, DocumentCalculator } from '@skylab/calculations/document';
import { DocumentItemChanges, DocumentItemChangesProperties } from '@skylab/calculations/document-item';
import { ContextKeys } from '../../common/context-keys';
import { CompanyParametersQuery } from '../../company/queries/company-parameters.query-base';
import { CurrencyCollectionQuery, CurrencyItem } from '../../currency/queries/currency.query-base';
import { AdvanceInvoiceAdjustmentDocumentGrossCalculator, AdvanceInvoiceDocumentGrossCalculator, AdvanceItem } from '../../documents/algorithms/document/advance-invoice-document-calculator';
import { MarginItem, MinimalMarginItem } from '../../documents/algorithms/item/item-margin-calculator';
import { DocumentCalculatorBoPart, DocumentCalculatorModelPart, DocumentItemParameters, convertItemToBaseCurrency } from '../../documents/bo-parts/document-calculator.bo-part';
import { RecalculateDocumentJoinPoint, recalculateDocumentJoinPointFilter } from '../../documents/utils/recalculate-document-join-point';
import { VatRateForSaleQuery } from '../../vat/queries/vat-rate-for-sale.query-base';
import { VatRateItem } from '../../vat/queries/vat-rate-item';
import { getDocumentCalculatorForSaleDocumentKind } from '../utils/sale-document-calculators-utils';

type DocumentItemModels = SaleDocumentItemModel & SaleAdvanceDocumentItemModel;

export interface SaleDocumentCalculatorModelPart extends DocumentCalculatorModelPart<DocumentItemModels> {
    advanceValue: number;
    advanceReturnValue: number;
    kind: SaleDocumentKind;
    vatSummary?: VatSummary & AdvanceSummary;
}

export class SaleDocumentCalculatorBoPart extends DocumentCalculatorBoPart<SaleDocumentCalculatorModelPart, DocumentItemModels> {
    public override async initializeAsync(context: BusinessObjectPartContext<SaleDocumentCalculatorModelPart>): Promise<void> {
        await super.initializeAsync(context);
        await this.bo.localContext.preload(VatRateForSaleQuery);

        this.registerCustomDocumentRecalculation();
    }

    private registerCustomDocumentRecalculation(): void {
        this.registerListening(this.weaver.register(RecalculateDocumentJoinPoint, recalculateDocumentJoinPointFilter(this.bo)()(
            () => this.recalculateDocumentModelAndRewrite(this.bo.model)
        )));
    }

    protected override recalculateTItemModelAndRewrite(itemModel: DocumentItemModels, exchangeRate: ExchangeRateModel, updatedProperty?: DocumentItemChangesProperties): void {
        //olekm: trzeba wyznaczyć koszty przed resztą obliczeń
        this.recalculateAndRewrite_cost(updatedProperty!, itemModel);
        super.recalculateTItemModelAndRewrite(itemModel, exchangeRate, updatedProperty);
    }

    private recalculateAndRewrite_cost(updatedProperty: DocumentItemChangesProperties, itemModel: DocumentItemModels) {
        const { precision } = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { id: this.bo.model.currencyId })!;

        if (DocumentItemChanges.unitCostChanged(updatedProperty) || DocumentItemChanges.quantityChanged(updatedProperty)) {
            itemModel.cost = Calc.round(itemModel.unitCost * itemModel.quantity, precision, MidpointRounding.AwayFromZero);
        } else if (DocumentItemChanges.costChanged(updatedProperty)) {
            itemModel.unitCost = Calc.round(itemModel.cost / itemModel.quantity, precision, MidpointRounding.AwayFromZero);
        }
    }

    public override get documentPropertiesThatTriggerRecalculation(): (keyof SaleDocumentCalculatorModelPart)[] {
        return [
            ...super.documentPropertiesThatTriggerRecalculation,
            'advanceValue',
            'vatSummary',
            'advanceReturnValue'
        ];
    }

    public override get itemPropertiesThatTriggerRecalculation(): (keyof DocumentItemModels)[] {
        return [
            ...super.itemPropertiesThatTriggerRecalculation,
            'unitCost',
            'cost',
        ];
    }

    protected recalculateDocumentModel(documentModel: SaleDocumentCalculatorModelPart): Readonly<{ inDocumentCurrency: CalculatedDocument, inBaseCurrency: CalculatedDocument }> {
        const result: { inDocumentCurrency: CalculatedDocument | null, inBaseCurrency: CalculatedDocument | null } = { inDocumentCurrency: null, inBaseCurrency: null };

        const { systemCurrency } = this.bo.localContext.getPreloaded(CompanyParametersQuery)!;
        const inForeignCurrency = documentModel.currencyId !== this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency })!.id;
        const { precision } = this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { id: documentModel.currencyId })!;

        const items = documentModel.items
            .map(documentItem => {
                const { rate } = this.getVatRateItem(documentItem.vatRateId);
                return this.convertDocumentItemToItem(documentItem, {
                    rate,
                    exchangeRate: inForeignCurrency && (documentModel.exchangeRate !== null) ? {
                        rate: documentModel.exchangeRate.rate,
                        units: documentModel.exchangeRate.units
                    } : { rate: 1, units: 1 },
                    calculationMethod: documentModel.calculationMethod,
                    precision
                });
            });

        const calculationContext = { precision };
        const calculator = getDocumentCalculatorForSaleDocumentKind(documentModel.kind, documentModel.calculationMethod, documentModel.issuedByVatPayer);
        if (calculator instanceof AdvanceInvoiceDocumentGrossCalculator || calculator instanceof AdvanceInvoiceAdjustmentDocumentGrossCalculator) {
            result.inDocumentCurrency = calculator.recalculate(items, {
                ...calculationContext,
                calculatePerAdvance: inForeignCurrency,
                advanceValue: documentModel.advanceValue,
                advanceReturnValue: documentModel.advanceReturnValue,
                vatSummary: documentModel.vatSummary
            });
        } else if (calculator instanceof DocumentCalculator) {
            result.inDocumentCurrency = calculator.recalculate(items, calculationContext);
        } else if (calculator instanceof DocumentAdjustmentCalculator) {
            result.inDocumentCurrency = calculator.recalculate(
                items,
                inForeignCurrency && (documentModel.exchangeRate !== null) ? documentModel.vatSummary!.currencyVatTable!.items : documentModel.vatSummary!.vatTable.items,
                calculationContext
            );
        }

        if (inForeignCurrency) {
            const { precision: systemCurrencyPrecision } = this.bo.localContext.getPreloaded<CurrencyItem>(ContextKeys.systemCurrency)!;
            const multiplier = (documentModel.exchangeRate?.rate ?? 1) / (documentModel.exchangeRate?.units ?? 1);

            const inBaseCurrencyItems = documentModel.items
                .map(item => convertItemToBaseCurrency(
                    this.convertDocumentItemToItem(item, {
                        rate: this.getVatRateItem(item.vatRateId).rate,
                        exchangeRate: { rate: 1, units: 1 },
                        calculationMethod: documentModel.calculationMethod,
                        precision
                    }),
                    inForeignCurrency && (documentModel.exchangeRate !== null) ? {
                        rate: documentModel.exchangeRate.rate,
                        units: documentModel.exchangeRate.units
                    } : { rate: 1, units: 1 },
                    systemCurrencyPrecision
                ));

            if (calculator instanceof AdvanceInvoiceDocumentGrossCalculator || calculator instanceof AdvanceInvoiceAdjustmentDocumentGrossCalculator) {
                const converted = inBaseCurrencyItems.map(x => <AdvanceItem>x);
                result.inBaseCurrency = calculator.recalculate(
                    converted,
                    {
                        ...calculationContext,
                        calculatePerAdvance: inForeignCurrency,
                        advanceValue: documentModel.advanceValue * multiplier,
                        advanceReturnValue: documentModel.advanceReturnValue * multiplier,
                        vatSummary: documentModel.vatSummary
                    });
            } else if (calculator instanceof DocumentCalculator) {
                result.inBaseCurrency = calculator.recalculate(inBaseCurrencyItems, calculationContext);
            } else if (calculator instanceof DocumentAdjustmentCalculator) {
                result.inBaseCurrency = calculator.recalculate(inBaseCurrencyItems, documentModel.vatSummary!.vatTable.items, calculationContext);
            }
        }

        return result as Readonly<{ inDocumentCurrency: CalculatedDocument, inBaseCurrency: CalculatedDocument }>;
    }

    protected override convertDocumentItemToItem(itemModel: DocumentItemModels, parameters: DocumentItemParameters): MinimalMarginItem {
        return {
            ...super.convertDocumentItemToItem(itemModel, parameters),
            unitCost: itemModel.unitCost,
            exchangeRate: parameters.exchangeRate.rate / parameters.exchangeRate.units,
            cost: itemModel.cost,
        };
    }

    protected override getItemModelToConvert_afterDiscount(itemModel: DocumentItemModels, parameters: DocumentItemParameters): MarginItem {
        return {
            ...super.getItemModelToConvert_afterDiscount(itemModel, parameters),
            unitCost: itemModel.unitCost,
            cost: itemModel.cost,
            exchangeRate: parameters.exchangeRate.rate / parameters.exchangeRate.units,
        };
    }

    protected override getItemModelToConvert_beforeDiscount(itemModel: DocumentItemModels, parameters: DocumentItemParameters): MarginItem  {
        return {
            ...super.getItemModelToConvert_beforeDiscount(itemModel, parameters),
            unitCost: itemModel.unitCost,
            cost: itemModel.cost,
            exchangeRate: parameters.exchangeRate.rate / parameters.exchangeRate.units,
        };
    }

    protected override rewriteVatTableItemToVatTableItemModel(item: UnboxCollection<CalculatedDocument['vatTableItems']>, model: VatTableItemModel): void {
        if (item.cost != null) {
            model.cost = item.cost;
        }
        super.rewriteVatTableItemToVatTableItemModel(item, model);
    }

    protected getVatRateItem(id: number): VatRateItem {
        return this.bo.localContext.firstPreloaded(VatRateForSaleQuery, { id })!;
    }
}
